import { ExclamationCircleFilled, CheckCircleFilled } from "@ant-design/icons";
import { notification } from "antd";
import React from "react";
import { LoadingContextType } from "../loading/LoadingContext";
import { apiConfig } from '../../config/api';
import { DateReviver } from '../../utils/DateReviver';
import { readSelectedStaffId } from '../staff/staffIdentityStorage';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public errors?: string[]
  ) {
    super(message);
    this.name = "ApiError";
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

interface RequestOptions {
  skipClientHeader?: boolean;
  customHeaders?: Record<string, string>;
  successMessage?: string;
  showLoading?: boolean;
  showError?: boolean;
  showSuccess?: boolean; // If false, no success notification will be shown
  // GET only. By default a failed `get` swallows the error and resolves to
  // undefined, which makes "the call failed" indistinguishable from "the server
  // said there's nothing". Set this to rethrow the ApiError (status included)
  // WITHOUT raising a notification, so a caller can tell the two apart and decide
  // its own UX — `showError` also rethrows, but always notifies with it.
  rethrow?: boolean;
}

export abstract class ClubBookingApiClient {
  private readonly getToken: () => Promise<string>;
  private readonly clientId: string | null;
  private readonly loadingContext?: LoadingContextType;

  constructor(getToken: () => Promise<string>, clientId: string | null, loadingContext?: LoadingContextType) {
    this.getToken = getToken;
    this.clientId = clientId;
    this.loadingContext = loadingContext;
  }

  private handleError(error: ApiError) {
    const description =
      error.errors && error.errors.length > 0 ? (
        <ul className="list-disc pl-4">
          {error.errors.map((err, index) => (
            <li key={index}>{err}</li>
          ))}
        </ul>
      ) : (
        error.message
      );

    notification.error({
      message: "Error",
      description,
      placement: "topRight",
      icon: <ExclamationCircleFilled style={{ color: "#ff4d4f" }} />,
      className: "bg-white border border-red-100 shadow-lg",
      duration: 10,
    });
  }

  private handleSuccess(message: string) {
    notification.success({
      message: "Success",
      description: message,
      placement: "topRight",
      icon: <CheckCircleFilled style={{ color: "#52c41a" }} />,
      className: "bg-white border border-green-100 shadow-lg",
    });
  }

  protected async getHeaders(options?: RequestOptions): Promise<Headers> {
    const token = await this.getToken();
    const headers = new Headers({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "Accept-Encoding": "gzip, deflate, br",
      ...options?.customHeaders,
    });

    if (!options?.skipClientHeader && this.clientId) {
      headers.set("X-Client-Id", this.clientId);
    }

    // Who the device is signed in as, for CRM endpoints that show "my work". Read at
    // request time rather than captured in the constructor so switching staff takes effect
    // immediately on service instances that were built before the switch. The API ignores
    // this whenever the login's own email matches a staff profile, so it can only ever
    // name a person on shared machines — see ResolveCurrentStaff.
    const selectedStaffId = readSelectedStaffId();
    if (selectedStaffId != null) {
      headers.set("X-Staff-Id", String(selectedStaffId));
    }

    return headers;
  }

  private buildUrl(endpoint: string, params?: Record<string, string | number>): string {
    const url = new URL(`${apiConfig.apiUrl}${endpoint}`);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, value.toString());
        }
      });
    }

    return url.toString();
  }

  // Helper method to handle loading state
  private handleLoadingState(options?: RequestOptions): boolean {
    const showLoading = options?.showLoading !== false; // Default to true
    if (showLoading && this.loadingContext) {
      this.loadingContext.startLoading();
    }
    return showLoading;
  }

  // Helper method to finish loading state
  private finishLoadingState(showLoading: boolean) {
    if (showLoading && this.loadingContext) {
      this.loadingContext.stopLoading();
    }
  }

  protected async get<T>(
    endpoint: string,
    params?: Record<string, string | number>,
    options?: RequestOptions
  ): Promise<T> {
    const showLoading = this.handleLoadingState(options);
    const headers = await this.getHeaders(options);
    const url = this.buildUrl(endpoint, params);

    try {
      const response = await fetch(url, { headers });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage: string;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData || errorText;
        } catch {
          errorMessage = errorText;
        }
        throw new ApiError(
          response.status,
          errorMessage,
          Array.isArray(errorMessage) ? errorMessage : undefined
        );
      }

      const jsonText = await response.text();
      if (!jsonText || jsonText.trim() === '') {
        return undefined as T;
      }
      return DateReviver.parse(jsonText);
    } catch (error) {
      if (options?.showError) {
        this.handleError(error as ApiError);
        throw error;
      }
      if (options?.rethrow) {
        throw error;
      }
      return undefined as T;
    } finally {
      this.finishLoadingState(showLoading);
    }
  }

  protected async post<T = void>(
    endpoint: string,
    data?: any,
    options?: RequestOptions
  ): Promise<T> {
    const showLoading = this.handleLoadingState(options);
    const headers = await this.getHeaders(options);
    try {
      const fetchOptions: RequestInit = {
        method: "POST",
        headers,
      };
      
      // Only include body if data is provided
      if (data !== undefined && data !== null) {
        fetchOptions.body = JSON.stringify(data);
      }
      
      const response = await fetch(`${apiConfig.apiUrl}${endpoint}`, fetchOptions);

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage: string;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData || errorText;
        } catch {
          errorMessage = errorText;
        }

        throw new ApiError(
          response.status,
          errorMessage,
          Array.isArray(errorMessage) ? errorMessage : undefined
        );
      }

      // Only show success message if showSuccess is not explicitly false
      if (options?.showSuccess !== false) {
        this.handleSuccess(options?.successMessage || "Success!");
      }

      if (response.status === 204 || response.headers.get("content-length") === "0") {
        return undefined as T;
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          const jsonText = await response.text();
          if (!jsonText || jsonText.trim() === '') {
            return undefined as T;
          }
          return DateReviver.parse(jsonText);
        } catch {
          return undefined as T;
        }
      }

      return undefined as T;
    } catch (error) {
      // `showError: false` lets a caller own the failure UX — e.g. a best-effort side call
      // whose own error toast would contradict the success message the caller just showed.
      if (options?.showError !== false) {
        if (error instanceof ApiError) {
          this.handleError(error);
        } else {
          this.handleError(new ApiError(500, "An unexpected error occurred"));
        }
      }
      throw error;
    } finally {
      this.finishLoadingState(showLoading);
    }
  }

  protected async put<T = void>(
    endpoint: string,
    data?: any,
    options?: RequestOptions
  ): Promise<T> {
    const showLoading = this.handleLoadingState(options);
    const headers = await this.getHeaders(options);
    try {
      const response = await fetch(`${apiConfig.apiUrl}${endpoint}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage: string;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData || errorText;
        } catch {
          errorMessage = errorText;
        }
        throw new ApiError(
          response.status,
          errorMessage,
          Array.isArray(errorMessage) ? errorMessage : undefined
        );
      }

      // Only show success message if showSuccess is not explicitly false
      if (options?.showSuccess !== false) {
        this.handleSuccess(options?.successMessage || "Success!");
      }

      if (response.status === 204 || response.headers.get("content-length") === "0") {
        return undefined as T;
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          const jsonText = await response.text();
          if (!jsonText || jsonText.trim() === '') {
            return undefined as T;
          }
          return DateReviver.parse(jsonText);
        } catch {
          return undefined as T;
        }
      }

      return undefined as T;
    } catch (error) {
      // `showError: false` lets a caller own the failure UX — e.g. a best-effort side call
      // whose own error toast would contradict the success message the caller just showed.
      if (options?.showError !== false) {
        if (error instanceof ApiError) {
          this.handleError(error);
        } else {
          this.handleError(new ApiError(500, "An unexpected error occurred"));
        }
      }
      throw error;
    } finally {
      this.finishLoadingState(showLoading);
    }
  }

  protected async delete<T = void>(
    endpoint: string,
    options?: RequestOptions
  ): Promise<T> {
    const showLoading = this.handleLoadingState(options);
    const headers = await this.getHeaders(options);
    try {
      const response = await fetch(`${apiConfig.apiUrl}${endpoint}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new ApiError(
          response.status,
          errorData.message || response.statusText,
          errorData.errors
        );
      }

      // Only show success message if showSuccess is not explicitly false
      if (options?.showSuccess !== false) {
        this.handleSuccess(options?.successMessage || "Success!");
      }

      if (response.status === 204 || response.headers.get("content-length") === "0") {
        return undefined as T;
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          const jsonText = await response.text();
          if (!jsonText || jsonText.trim() === '') {
            return undefined as T;
          }
          return DateReviver.parse(jsonText);
        } catch {
          return undefined as T;
        }
      }

      return undefined as T;
    } catch (error) {
      // `showError: false` lets a caller own the failure UX — e.g. a best-effort side call
      // whose own error toast would contradict the success message the caller just showed.
      if (options?.showError !== false) {
        if (error instanceof ApiError) {
          this.handleError(error);
        } else {
          this.handleError(new ApiError(500, "An unexpected error occurred"));
        }
      }
      throw error;
    } finally {
      this.finishLoadingState(showLoading);
    }
  }
}